import { Send } from 'lucide-react';
import { useState } from 'react';
import { AgentHandoff } from '../../components/jobs/AgentHandoff';
import { Modal, useToast } from '../../components/ui/overlays';
import { Button, Field, Input, Select, Switch, Textarea } from '../../components/ui/primitives';
import { useCreateCampaign, useLeadTypes, useMailboxes, useTemplates } from '../../hooks/queries';

/**
 * Queues outreach for a selected set of leads: creates a campaign, one task per lead and an
 * OUTREACH job. From there an MCP agent writes the emails.
 */
interface QueueOutreachProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  leadIds: string[];
  onQueued?: () => void;
}

export const QueueOutreachModal = (props: QueueOutreachProps) => props.open ? <QueueOutreachForm {...props} /> : null;

const QueueOutreachForm = ({
  open,
  onClose,
  projectId,
  leadIds,
  onQueued,
}: QueueOutreachProps) => {
  const createCampaign = useCreateCampaign(projectId);
  const { data: mailboxes } = useMailboxes(projectId);
  const { data: templates } = useTemplates(projectId);
  const { data: leadTypes } = useLeadTypes(projectId);
  const toast = useToast();
  const [queuedCount, setQueuedCount] = useState<number | null>(null);

  const [form, setForm] = useState({
    name: `Outreach ${new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`,
    instructions: '',
    mailboxId: '',
    templateId: '',
    leadTypeId: '',
    requireApproval: true,
    spacingMinutes: 5,
    startAt: '',
  });

  const submit = async () => {
    if (createCampaign.isPending) return;
    if (!leadIds.length) {
      toast.error('Select at least one lead first');
      return;
    }
    try {
      const campaign = await createCampaign.mutateAsync({
        name: form.name.trim() || 'Outreach',
        leadIds,
        instructions: form.instructions || undefined,
        mailboxId: form.mailboxId || undefined,
        templateId: form.templateId || undefined,
        leadTypeId: form.leadTypeId || undefined,
        requireApproval: form.requireApproval,
        spacingSeconds: Math.round(form.spacingMinutes * 60),
        scheduledStartAt: form.startAt ? new Date(form.startAt).toISOString() : undefined,
      });
      setQueuedCount(campaign.taskCount ?? leadIds.length);
      onQueued?.();
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!createCampaign.isPending) onClose(); }}
      title={queuedCount !== null ? 'Outreach job ready' : 'Queue outreach'}
      description={queuedCount !== null ? 'Start your connected agent to draft the emails.' : `${leadIds.length} lead${leadIds.length === 1 ? '' : 's'} selected. An agent will write one email per lead.`}
      footer={
        queuedCount !== null ? <Button onClick={onClose}>Done</Button> : <>
          <Button variant="ghost" disabled={createCampaign.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={createCampaign.isPending}
            disabled={!leadIds.length || form.name.trim().length < 2}
            icon={<Send className="h-4 w-4" />}
          >
            Queue outreach
          </Button>
        </>
      }
    >
      {queuedCount !== null ? <AgentHandoff projectId={projectId} type="OUTREACH" summary={`${queuedCount} ${queuedCount === 1 ? 'lead' : 'leads'} queued for outreach`} onClose={onClose} /> : <fieldset disabled={createCampaign.isPending} className="space-y-4">
        <Field label="Campaign name" htmlFor="campaign-name">
          <Input
            id="campaign-name"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </Field>

        <Field
          label="Extra instructions"
          htmlFor="campaign-instructions"
          hint="Added on top of the project and lead-type outreach prompts. Use it for the angle of this specific batch."
        >
          <Textarea
            id="campaign-instructions"
            rows={4}
            className="prompt-input"
            value={form.instructions}
            placeholder="Mention the spring campaign and that onboarding takes a day. Lead with the referral fee, not the product tour."
            onChange={(event) => setForm({ ...form, instructions: event.target.value })}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Send from" htmlFor="campaign-mailbox">
            <Select
              id="campaign-mailbox"
              value={form.mailboxId}
              onChange={(event) => setForm({ ...form, mailboxId: event.target.value })}
            >
              <option value="">Project default</option>
              {(mailboxes ?? []).map((mailbox) => (
                <option key={mailbox.id} value={mailbox.id}>
                  {mailbox.fromEmail}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Template to follow"
            htmlFor="campaign-template"
            hint={
              templates?.length
                ? 'Manage these under Email templates.'
                : 'None configured yet — create one under Email templates.'
            }
          >
            <Select
              id="campaign-template"
              value={form.templateId}
              onChange={(event) => setForm({ ...form, templateId: event.target.value })}
            >
              <option value="">Best match for the lead type</option>
              {(templates ?? []).map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                  {template.kind === 'PROMPT' ? ' · instructions' : ''}
                  {template.isDefault ? ' · default' : ''}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Lead type context"
            htmlFor="campaign-leadtype"
            hint="Only needed when the leads have no type of their own."
          >
            <Select
              id="campaign-leadtype"
              value={form.leadTypeId}
              onChange={(event) => setForm({ ...form, leadTypeId: event.target.value })}
            >
              <option value="">From each lead</option>
              {(leadTypes ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Minutes between emails"
            htmlFor="campaign-spacing"
            hint="On top of the mailbox rate limits."
          >
            <Input
              id="campaign-spacing"
              type="number"
              min={0}
              max={1440}
              value={form.spacingMinutes}
              onChange={(event) =>
                setForm({ ...form, spacingMinutes: Number(event.target.value) || 0 })
              }
            />
          </Field>

          <Field
            label="Start no earlier than"
            htmlFor="campaign-start"
            hint="Leave empty to start as soon as the drafts are ready."
            className="sm:col-span-2"
          >
            <Input
              id="campaign-start"
              type="datetime-local"
              value={form.startAt}
              onChange={(event) => setForm({ ...form, startAt: event.target.value })}
            />
          </Field>
        </div>

        <div className="rounded-lg border border-line bg-raised px-4 py-3">
          <Switch
            checked={form.requireApproval}
            onChange={(value) => setForm({ ...form, requireApproval: value })}
            label="Review each email before it goes out"
            description={
              form.requireApproval
                ? 'Emails land in Emails → Drafts and wait for you to approve them.'
                : 'Emails are scheduled automatically as soon as the agent writes them.'
            }
          />
        </div>
      </fieldset>}
    </Modal>
  );
};
